import TagIcon from "@/components/TagIcon";
import { asText, isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

const ProjectCard = ({ project, description, isRow = false }) => {
  const { preview_image, preview_title, preview_description, type } = project.data;
  const name = isFilled.richText(preview_title) ? asText(preview_title) : "";
  const previewText = isFilled.richText(preview_description)
    ? asText(preview_description)
    : "";
  const heading = [name, previewText].filter(Boolean).join(" : ");

  if (!isFilled.image(preview_image) && !heading) {
    return null;
  }

  return (
    <PrismicNextLink
      prefetch={false}
      field={project}
      className={`flex min-w-0 flex-1 flex-col items-start gap-5 ${isRow ? "lg:flex-row lg:items-center lg:gap-10" : ""}`}
    >
      {isFilled.image(preview_image) && (
        <div className={`relative h-[185px] w-full shrink-0 overflow-hidden rounded-[10px] ${isRow ? "lg:w-[230px]" : ""}`}>
          <PrismicNextImage
            field={preview_image}
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 100vw, 378px"
          />
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-3">
        {type && <TagIcon tag={type} />}
        {heading && (
          <h3 className="p-lg-semibold tracking-[0.2px] text-bleu">{heading}</h3>
        )}
        {isFilled.richText(description) && (
          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }) => (
                <p className="p-base-n lt text-bleu-90">{children}</p>
              ),
            }}
          />
        )}
      </div>
    </PrismicNextLink>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.BlogSliceProjectPrimary} ProjectPrimary
 * @param {{ primary: ProjectPrimary }} props
 */
const Project = ({ primary }) => {
  const items = (primary.projects || [])
    .filter(({ project }) => isFilled.contentRelationship(project) && Boolean(project.data))
    .slice(0, 2);

  return (
    <div className="flex w-full flex-col items-start gap-[30px]">
      {isFilled.richText(primary.title) && (
        <PrismicRichText
          field={primary.title}
          components={{
            heading2: ({ children }) => (
              <h2 className="font-body text-[22px] font-medium leading-[26px] tracking-[0.2px] text-bleu">
                {children}
              </h2>
            ),
          }}
        />
      )}
      {items.length > 0 && (
        <div className="flex w-full flex-col gap-10 lg:flex-row lg:gap-6">
          {items.map(({ project, description }, index) => (
            <ProjectCard
              key={index}
              project={project}
              description={description}
              isRow={items.length === 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Project;
